import { Link } from "react-router-dom";
import { ChevronRight, User, Users } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { Card } from "@/components/ui/card";
import { PageContainer, SectionStack } from "@/components/layout";

/**
 * `/settings` — the index for the three settings surfaces.
 *
 * **W41.0: this page was a placeholder.** It rendered exactly one line of copy —
 * 「W1 占位版。主題切換、通知、匯出等功能將於 W2 實裝。」 — and W2 is several
 * months past. A user opening Settings was told a promise that had quietly
 * expired, and the three sub-pages that promise referred to (`/settings/account`,
 * `/settings/pair`, `/settings/pair/dissolve`) have all shipped. It also had **no
 * test asserting it**, which is how a placeholder outlives the sprint that was
 * supposed to remove it.
 *
 * Two things it deliberately does *not* claim:
 *
 *   · **主題 / 通知 / 匯出 are not listed.** They are not built. Listing them as
 *     rows that go nowhere would recreate the same defect in a new shape — and
 *     W31.1's lesson (`DESIGN.md §13`, a dead-end link is worse than a missing
 *     one) says the same. The mockup's `nav-future-theme` slot exists for
 *     「即將」 rows in the *module menu*, which is a different surface; a
 *     「即將」 row on the settings index would promise a route, not a module.
 *   · **There is no `/settings` entry for 解散配對.** Dissolving a pair is
 *     reached from the pair settings page, where the consequences are visible.
 *     It is a destructive action, not a settings category.
 */
const ROWS = [
  {
    to: "/settings/account",
    testId: "settings-account-link",
    label: "帳號",
    description: "電子郵件、密碼、帳號刪除",
    Icon: User,
  },
  {
    to: "/settings/pair",
    testId: "settings-pair-link",
    label: "配對",
    description: "配對狀態、邀請連結、QR code",
    Icon: Users,
  },
] as const;

export function SettingsPage() {
  return (
    <div data-testid="settings-page">
      <PageHero eyebrow="個人化" title="設定" />
      <PageContainer vertical="default">
        <SectionStack density="default">
          <Card className="overflow-hidden p-0">
            {ROWS.map(({ to, testId, label, description, Icon }, idx) => (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className={
                  "tap-target press flex items-center gap-3 px-s-5 py-4 transition-colors duration-quick ease-paper " +
                  "hover:bg-black/4 focus-visible:bg-black/4 " +
                  (idx > 0 ? "border-t border-hairline" : "")
                }
              >
                <span
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper-deep text-ink-2"
                >
                  <Icon size={20} strokeWidth={1.5} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-body font-semibold text-ink-1">{label}</span>
                  <span className="block text-meta text-ink-3">{description}</span>
                </span>
                <ChevronRight size={20} strokeWidth={1.5} aria-hidden className="shrink-0 text-ink-3" />
              </Link>
            ))}
          </Card>

          {/* Kept as a visible fact rather than a promise. The old copy said
              「將於 W2 實裝」 — a future tense that stopped being true. This one
              describes what is and is not here, which cannot expire. */}
          <p data-testid="settings-not-shipped-note" className="px-s-1 text-meta text-ink-3">
            主題、通知與匯出尚未實作。設定入口在日後加入時會出現在這個列表。
          </p>
        </SectionStack>
      </PageContainer>
    </div>
  );
}
